import React, { useEffect, useMemo } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Flex, Col } from 'antd'
import { useCheckPermission } from '../hooks/Permissions'
import { useUsageMonitorPage } from '../hooks/UsageMonitorPage'
import {
  mergeIntervalsBySessionId,
  buildUserOptions,
  buildDateOptions,
} from './utils'
import GroupedByUserGraph from './Widgets/GroupedByUserGraph'
import DailyActiveUsers from './Widgets/DailyActiveUsers'
import { requireAuthentication } from '../helpers/authentication'
import ReportPageLayout from './ReportPageLayout'
import GlobalSearchTable from './Widgets/GlobalSearchTable'
import ActiveFiltersDropdown from './Widgets/ActiveFiltersDropdown'

const GLOBAL_SEARCH_EVENT_TYPES = [
  'GLOBAL_SEARCH_PAGE_LOADED',
  'GLOBAL_SEARCH_PAGE_TIME_SPENT',
  'GLOBAL_SEARCH_PERFORMED',
]

const GlobalSearchUsagePage = injectIntl(() => {
  const checkPermission = useCheckPermission()
  const {
    plantFilter,
    userIdentifier,
    selectedUser,
    setSelectedUser,
    selectedDate,
    setSelectedDate,
    endTimeFilter,
    setStartTimeFilter,
    setEndTimeFilter,
    sortedData,
    setSortedData,
    lastUpdatedTime,
    setLastUpdatedTime,
    allUsageData,
    isLoading,
    refetch,
  } = useUsageMonitorPage()

  useEffect(() => {
    if (!isLoading && allUsageData) {
      setLastUpdatedTime(new Date())
      const sortedDataCopy = {}
      const relevantData = allUsageData.filter((d) =>
        GLOBAL_SEARCH_EVENT_TYPES.includes(d.eventType),
      )
      const mergedData = mergeIntervalsBySessionId(
        relevantData,
        'GLOBAL_SEARCH_PAGE_TIME_SPENT',
        'timeOnPage',
      )
      mergedData.forEach((data) => {
        if (sortedDataCopy[data.eventType]) {
          sortedDataCopy[data.eventType].push(data)
        } else {
          sortedDataCopy[data.eventType] = [data]
        }
      })
      setSortedData(sortedDataCopy)
    }
  }, [isLoading, allUsageData])

  const userOptions = useMemo(
    () =>
      buildUserOptions(sortedData, 'GLOBAL_SEARCH_PERFORMED', userIdentifier),
    [sortedData, userIdentifier],
  )
  const dateOptions = useMemo(
    () => buildDateOptions(sortedData, 'GLOBAL_SEARCH_PERFORMED'),
    [sortedData],
  )

  if (!checkPermission('page-global-search-usage:get')) {
    return (
      <div>
        <FormattedMessage
          id="globalSearchUsage.accessDenied"
          defaultMessage="Access Denied: You do not have permission to view global search usage."
        />
      </div>
    )
  }

  const hasData = Object.keys(sortedData).length !== 0

  return (
    <ReportPageLayout
      pageId="global-search-usage-page"
      breadcrumbLabel={{
        id: 'globalSearchUsage.header',
        defaultMessage: 'Reports',
      }}
      breadcrumbSubLabel={{
        id: 'globalSearchUsage.subLabel',
        defaultMessage: 'Global Search Usage',
      }}
      onFilterChange={(start, end) => {
        setStartTimeFilter(start)
        setEndTimeFilter(end)
      }}
      onSync={() => refetch()}
      lastUpdatedTime={lastUpdatedTime}
    >
      {hasData && (
        <>
          <Flex
            vertical={false}
            gap={4}
            className="widget-row"
            justify="space-between"
          >
            <Col className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="globalSearchUsage.dailyGlobalSearchUsage"
                    defaultMessage="Daily Global Search Usage"
                  />
                </div>
              </Flex>
              <div className="widget-body">
                <DailyActiveUsers
                  data={sortedData['GLOBAL_SEARCH_PERFORMED']}
                  endDate={endTimeFilter}
                  onDateSelect={setSelectedDate}
                />
              </div>
            </Col>
            <Col className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="globalSearchUsage.searchesPerUser"
                    defaultMessage="Searches Per User"
                  />
                </div>
              </Flex>
              <div className="widget-body">
                <GroupedByUserGraph
                  data={sortedData['GLOBAL_SEARCH_PERFORMED']}
                  xFieldLabel={'Users'}
                  groupedValKey={'searchString'}
                  yFieldLabel="Searches"
                  stat={'count'}
                  user_identifier={userIdentifier}
                  handleBarClick={(user) => {
                    if (!user || user === 'Unknown' || user === selectedUser) {
                      setSelectedUser(null)
                    } else {
                      setSelectedUser(user)
                    }
                  }}
                />
              </div>
            </Col>
          </Flex>
          <Flex
            vertical={false}
            gap={4}
            className="widget-row"
            justify="space-between"
            style={{ height: 'auto', minHeight: '33%' }}
          >
            <Col span={24} className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="globalSearchUsage.sessionTable"
                    defaultMessage="Session Details"
                  />
                </div>
                <ActiveFiltersDropdown
                  filters={[
                    {
                      key: 'user',
                      label: 'User',
                      value: selectedUser,
                      options: userOptions,
                      onChange: setSelectedUser,
                    },
                    {
                      key: 'date',
                      label: 'Date',
                      value: selectedDate,
                      options: dateOptions,
                      onChange: setSelectedDate,
                    },
                  ]}
                />
              </Flex>
              <div className="widget-body">
                <GlobalSearchTable
                  data={allUsageData}
                  plantFilter={plantFilter}
                  selectedDate={selectedDate}
                  selectedUser={selectedUser}
                  userIdentifier={userIdentifier}
                />
              </div>
            </Col>
          </Flex>
        </>
      )}
    </ReportPageLayout>
  )
})

export default requireAuthentication(GlobalSearchUsagePage)
